import { t, Instance, flow } from 'mobx-state-tree';
import type {
  CitationResponse,
  SourceResponse,
  SuggestionCitationSummary,
  SuggestionResponse,
  SuggestionStatus
} from 'we-tree-api-client/v2';
import { SourceType, SuggestionStatus as SuggestionStatusEnum } from 'we-tree-api-client/v2';
import { suggestionsApi } from '@/api/suggestionsWrapper';
import { sourcesApi, citationsApi } from '@/api/sourcesWrapper';
import { mediaApi } from '@/api/mediaWrapper';
import { peopleApi } from '@/api/peopleWrapper';
import { domainStore } from '@/stores/DomainStore';
import { generateUUID } from '@/utils/uuid';
import {
  buildPersonDiffPayload,
  diffPayloadHasChanges,
  isSuggestionTargetStale,
  type CreatePersonSuggestionPayload,
  type CreatePersonSuggestionResult,
  type SuggestionEvidenceDraft,
  buildFactDiffOps,
  buildLifeEventDiffOps,
  mergeSuggestionDiffPayload
} from '@/types/suggestion';
import { SuggestionModel, createSuggestionModelFromResponse } from '@/stores/models/SuggestionModel';

const ACTIONABLE_STATUSES: SuggestionStatus[] = [SuggestionStatusEnum.Pending, SuggestionStatusEnum.ChangesRequested];

function currentUserId(): string | undefined {
  const id = domainStore.user?.id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

const MySuggestionPreviewModel = t.model('MySuggestionPreviewModel', {
  id: t.identifier,
  title: t.maybeNull(t.string),
  status: t.string,
  targetId: t.string,
  targetType: t.string,
  createdAt: t.string,
  updatedAt: t.string
});

const MySuggestionsFiltersModel = t.model('MySuggestionsFiltersModel', {
  status: t.optional(
    t.enumeration(['all', 'pending', 'changes_requested', 'approved', 'rejected', 'cancelled']),
    'all'
  ),
  page: t.optional(t.number, 1),
  pageSize: t.optional(t.number, 20)
});

function toPreview(item: SuggestionResponse): Instance<typeof MySuggestionPreviewModel> {
  return MySuggestionPreviewModel.create({
    id: item.id,
    title: item.title ?? null,
    status: item.status,
    targetId: item.targetId,
    targetType: item.targetType,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt
  });
}

function sortByCreatedAtDesc(a: SuggestionResponse, b: SuggestionResponse): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

async function resolveEvidenceCitationId(familyId: string, draft: SuggestionEvidenceDraft): Promise<string> {
  let sourceId = draft.sourceId?.trim() || '';

  if (!sourceId) {
    const title = draft.newSource?.title?.trim();
    const url = draft.newSource?.url?.trim();
    if (!title || !url) {
      throw new Error('New source requires a title and URL');
    }
    const createSourceResult = await sourcesApi.createSource(familyId, {
      title,
      url,
      type: SourceType.Website
    });
    if (!createSourceResult.success || !createSourceResult.data) {
      throw (!createSourceResult.success && createSourceResult.error) || new Error('Failed to create source');
    }
    sourceId = createSourceResult.data.id;
  }

  if (draft.citationId?.trim()) {
    return draft.citationId.trim();
  }

  const createCitationResult = await sourcesApi.createCitation(familyId, sourceId, {
    detailLocator: draft.newCitation?.detailLocator?.trim() || undefined,
    notes: draft.newCitation?.notes?.trim() || undefined,
    mediaAssetId: draft.newCitation?.mediaAssetId?.trim() || undefined
  });
  if (!createCitationResult.success || !createCitationResult.data) {
    throw (!createCitationResult.success && createCitationResult.error) || new Error('Failed to create citation');
  }
  return createCitationResult.data.id;
}

async function attachEvidenceDrafts(
  familyId: string,
  suggestionId: string,
  evidence: SuggestionEvidenceDraft[]
): Promise<{ success: true; data: SuggestionCitationSummary[] } | { success: false; error: Error }> {
  if (!evidence.length) {
    return { success: true, data: [] };
  }

  const citations: { citationId: string; targetPath?: string; supports?: boolean }[] = [];
  for (const draft of evidence) {
    const citationId = await resolveEvidenceCitationId(familyId, draft);
    citations.push({
      citationId,
      targetPath: draft.targetPath?.trim() || undefined,
      supports: draft.supports ?? true
    });
  }

  const result = await suggestionsApi.attachSuggestionCitations(familyId, suggestionId, { citations });
  if (result.success && result.data) {
    return { success: true, data: result.data };
  }
  return {
    success: false,
    error: (!result.success && result.error) || new Error('Failed to attach evidence')
  };
}

export const SuggestionsStore = t
  .model('SuggestionsStore', {
    suggestionsById: t.map(SuggestionModel),
    isSubmitting: t.optional(t.boolean, false),
    error: t.maybeNull(t.string),

    myList: t.array(MySuggestionPreviewModel),
    myListTotal: t.optional(t.number, 0),
    myListFilters: t.optional(MySuggestionsFiltersModel, {}),
    isLoadingMyList: t.optional(t.boolean, false),
    myListError: t.maybeNull(t.string),
    selectedSuggestionId: t.maybeNull(t.string),
    selectedSuggestionDetail: t.maybeNull(t.frozen<SuggestionResponse>()),
    isLoadingDetail: t.optional(t.boolean, false),
    detailError: t.maybeNull(t.string),
    isCancelling: t.optional(t.boolean, false),

    pendingAuthorTotal: t.optional(t.number, 0),
    pendingAuthorPreview: t.array(MySuggestionPreviewModel),
    isLoadingPendingSummary: t.optional(t.boolean, false),
    pendingSummaryFamilyId: t.maybeNull(t.string),

    memberPendingTotal: t.optional(t.number, 0),
    memberPendingHasChangesRequested: t.optional(t.boolean, false),
    memberPendingFamilyMemberId: t.maybeNull(t.string),
    isLoadingMemberPending: t.optional(t.boolean, false)
  })
  .views((self) => ({
    getSuggestion(suggestionId: string): Instance<typeof SuggestionModel> | undefined {
      return self.suggestionsById.get(suggestionId);
    },

    get hasPendingAuthorSuggestions(): boolean {
      return self.pendingAuthorTotal > 0;
    },

    get myListTotalPages(): number {
      return Math.max(1, Math.ceil(self.myListTotal / self.myListFilters.pageSize));
    }
  }))
  .actions((self) => ({
    clearError() {
      self.error = null;
    },

    setMyListStatusFilter(status: Instance<typeof MySuggestionsFiltersModel>['status']) {
      self.myListFilters.status = status;
      self.myListFilters.page = 1;
      self.selectedSuggestionId = null;
      self.selectedSuggestionDetail = null;
    },

    setMyListPage(page: number) {
      self.myListFilters.page = page;
    },

    selectSuggestion(suggestionId: string | null) {
      if (suggestionId === self.selectedSuggestionId) return;
      self.selectedSuggestionId = suggestionId;
      self.selectedSuggestionDetail = null;
      self.detailError = null;
    },

    checkPersonTargetFreshness: flow(function* (familyMemberId: string, baseUpdatedAt: string | null | undefined) {
      try {
        const result = yield peopleApi.getPerson(familyMemberId);
        if (!result.success || !result.data) {
          return { stale: false as const };
        }
        return {
          stale: isSuggestionTargetStale(baseUpdatedAt, result.data.updatedAt)
        };
      } catch {
        return { stale: false as const };
      }
    }),

    listEvidenceSources: flow(function* (familyId: string) {
      try {
        const result = yield sourcesApi.listSources(familyId, { page: 1, pageSize: 100 });
        if (result.success && result.data) {
          return { success: true, data: (result.data.items ?? []) as SourceResponse[] };
        }
        return {
          success: false,
          error: result.error ?? new Error('Failed to load sources'),
          data: [] as SourceResponse[]
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to load sources'),
          data: [] as SourceResponse[]
        };
      }
    }),

    listEvidenceCitationsForSource: flow(function* (familyId: string, sourceId: string) {
      try {
        const result = yield citationsApi.listCitations(familyId, {
          sourceId,
          page: 1,
          pageSize: 100
        });
        if (result.success && result.data) {
          return { success: true, data: (result.data.items ?? []) as CitationResponse[] };
        }
        return {
          success: false,
          error: result.error ?? new Error('Failed to load citations'),
          data: [] as CitationResponse[]
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to load citations'),
          data: [] as CitationResponse[]
        };
      }
    }),

    uploadEvidenceMedia: flow(function* (familyId: string, file: File) {
      try {
        const result = yield mediaApi.uploadCitationImage(familyId, file);
        if (result.success && result.data) {
          return { success: true, data: result.data };
        }
        return {
          success: false,
          error: result.error ?? new Error('Failed to upload evidence image')
        };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to upload evidence image')
        };
      }
    }),

    attachEvidenceToSuggestion: flow(function* (
      familyId: string,
      suggestionId: string,
      evidence: SuggestionEvidenceDraft[]
    ) {
      try {
        return yield attachEvidenceDrafts(familyId, suggestionId, evidence);
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to attach evidence')
        };
      }
    }),

    createPersonSuggestion: flow(function* (familyId: string, payload: CreatePersonSuggestionPayload) {
      self.isSubmitting = true;
      self.error = null;

      try {
        const personPayload = buildPersonDiffPayload(payload.originalPerson, payload.fieldValues);
        const factOps = buildFactDiffOps(
          payload.factCreates ?? [],
          payload.factUpdates ?? [],
          payload.factDeletes ?? []
        );
        const lifeEventOps = buildLifeEventDiffOps(
          payload.lifeEventCreates ?? [],
          payload.lifeEventUpdates ?? [],
          payload.lifeEventDeletes ?? []
        );
        const diffPayload = mergeSuggestionDiffPayload(personPayload, factOps, lifeEventOps);

        if (!diffPayloadHasChanges(diffPayload)) {
          return { success: false, error: new Error('No changes to suggest') } as CreatePersonSuggestionResult;
        }

        const suggestionId = generateUUID();
        const title =
          payload.title?.trim() ||
          `Suggested changes for ${payload.originalPerson.givenName} ${payload.originalPerson.surname ?? ''}`.trim();

        const result = yield suggestionsApi.createSuggestion(familyId, {
          id: suggestionId,
          targetType: 'family_member',
          targetId: payload.familyMemberId,
          suggestionType: 'update',
          diffPayload,
          title,
          description: payload.description?.trim() || undefined
        });

        if (!result.success || !result.data) {
          const message = result.error?.getUserMessage?.() ?? 'Failed to submit suggestion';
          self.error = message;
          return {
            success: false,
            error: result.error ?? new Error(message)
          } as CreatePersonSuggestionResult;
        }

        let suggestionData = result.data as SuggestionResponse;
        const model = createSuggestionModelFromResponse(suggestionData);
        self.suggestionsById.set(model.id, model);

        const evidence = payload.evidence?.filter((item) => {
          if (item.citationId?.trim()) return true;
          if (item.sourceId?.trim() && item.newCitation) return true;
          if (item.newSource?.title?.trim() && item.newSource?.url?.trim()) return true;
          return false;
        });

        if (evidence && evidence.length > 0) {
          try {
            const attachResult: Awaited<ReturnType<typeof attachEvidenceDrafts>> = yield attachEvidenceDrafts(
              familyId,
              suggestionData.id,
              evidence
            );
            if (attachResult.success) {
              const refreshed = yield suggestionsApi.getSuggestion(familyId, suggestionData.id);
              if (refreshed.success && refreshed.data) {
                suggestionData = refreshed.data as SuggestionResponse;
                self.suggestionsById.set(suggestionData.id, createSuggestionModelFromResponse(suggestionData));
              }
              return { success: true, data: suggestionData } as CreatePersonSuggestionResult;
            }

            const evidenceMessage =
              (attachResult.error as { getUserMessage?: () => string } | undefined)?.getUserMessage?.() ??
              attachResult.error?.message ??
              'Could not attach evidence';
            return {
              success: true,
              data: suggestionData,
              evidenceFailed: true,
              evidenceErrorMessage: evidenceMessage
            } as CreatePersonSuggestionResult;
          } catch (evidenceError) {
            const evidenceMessage =
              evidenceError instanceof Error ? evidenceError.message : 'Could not attach evidence';
            return {
              success: true,
              data: suggestionData,
              evidenceFailed: true,
              evidenceErrorMessage: evidenceMessage
            } as CreatePersonSuggestionResult;
          }
        }

        return { success: true, data: suggestionData } as CreatePersonSuggestionResult;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to submit suggestion';
        self.error = message;
        return {
          success: false,
          error: error instanceof Error ? error : new Error(message)
        } as CreatePersonSuggestionResult;
      } finally {
        self.isSubmitting = false;
      }
    }),

    loadSuggestion: flow(function* (familyId: string, suggestionId: string) {
      try {
        const result = yield suggestionsApi.getSuggestion(familyId, suggestionId);
        if (result.success && result.data) {
          const model = createSuggestionModelFromResponse(result.data);
          self.suggestionsById.set(model.id, model);
          return { success: true, data: result.data };
        }
        return { success: false, error: result.error };
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error : new Error('Failed to load suggestion')
        };
      }
    }),

    loadMySuggestions: flow(function* (familyId: string, options?: { targetId?: string }) {
      self.isLoadingMyList = true;
      self.myListError = null;

      try {
        const createdBy = currentUserId();
        if (!createdBy) {
          self.myList.clear();
          self.myListTotal = 0;
          self.myListError = 'Authentication required';
          return { success: false };
        }

        const statusFilter =
          self.myListFilters.status === 'all' ? undefined : (self.myListFilters.status as SuggestionStatus);

        const result = yield suggestionsApi.listSuggestions(familyId, {
          createdBy,
          status: statusFilter,
          targetType: options?.targetId ? 'family_member' : undefined,
          targetId: options?.targetId,
          page: self.myListFilters.page,
          pageSize: self.myListFilters.pageSize,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        });

        if (result.success && result.data) {
          const items: SuggestionResponse[] = result.data.items ?? [];
          self.myList.replace(items.map(toPreview));
          self.myListTotal = result.data.total ?? items.length;
          for (const item of items) {
            self.suggestionsById.set(item.id, createSuggestionModelFromResponse(item));
          }
          return { success: true };
        }

        self.myList.clear();
        self.myListTotal = 0;
        self.myListError = result.error?.getUserMessage?.() ?? 'Failed to load suggestions';
        return { success: false };
      } catch (error) {
        self.myList.clear();
        self.myListTotal = 0;
        self.myListError = error instanceof Error ? error.message : 'Failed to load suggestions';
        return { success: false };
      } finally {
        self.isLoadingMyList = false;
      }
    }),

    loadMyPendingSummary: flow(function* (familyId: string) {
      self.isLoadingPendingSummary = true;

      try {
        const createdBy = currentUserId();
        if (!createdBy) {
          self.pendingSummaryFamilyId = familyId;
          self.pendingAuthorTotal = 0;
          self.pendingAuthorPreview.clear();
          return;
        }

        const [pendingResult, changesResult] = yield Promise.all([
          suggestionsApi.listSuggestions(familyId, {
            createdBy,
            status: SuggestionStatusEnum.Pending,
            page: 1,
            pageSize: 5,
            sortBy: 'createdAt',
            sortOrder: 'desc'
          }),
          suggestionsApi.listSuggestions(familyId, {
            createdBy,
            status: SuggestionStatusEnum.ChangesRequested,
            page: 1,
            pageSize: 5,
            sortBy: 'createdAt',
            sortOrder: 'desc'
          })
        ]);

        const pendingItems: SuggestionResponse[] =
          pendingResult.success && pendingResult.data ? (pendingResult.data.items ?? []) : [];
        const changesItems: SuggestionResponse[] =
          changesResult.success && changesResult.data ? (changesResult.data.items ?? []) : [];
        const pendingTotal =
          pendingResult.success && pendingResult.data ? (pendingResult.data.total ?? pendingItems.length) : 0;
        const changesTotal =
          changesResult.success && changesResult.data ? (changesResult.data.total ?? changesItems.length) : 0;

        const merged = [...pendingItems, ...changesItems].sort(sortByCreatedAtDesc).slice(0, 5);

        self.pendingSummaryFamilyId = familyId;
        self.pendingAuthorTotal = pendingTotal + changesTotal;
        self.pendingAuthorPreview.replace(merged.map(toPreview));
      } catch {
        self.pendingSummaryFamilyId = familyId;
        self.pendingAuthorTotal = 0;
        self.pendingAuthorPreview.clear();
      } finally {
        self.isLoadingPendingSummary = false;
      }
    }),

    loadPendingForMember: flow(function* (familyId: string, familyMemberId: string) {
      self.isLoadingMemberPending = true;

      try {
        const createdBy = currentUserId();
        if (!createdBy) {
          self.memberPendingFamilyMemberId = familyMemberId;
          self.memberPendingTotal = 0;
          self.memberPendingHasChangesRequested = false;
          return;
        }

        const [pendingResult, changesResult] = yield Promise.all([
          suggestionsApi.listSuggestions(familyId, {
            createdBy,
            status: SuggestionStatusEnum.Pending,
            targetType: 'family_member',
            targetId: familyMemberId,
            page: 1,
            pageSize: 1
          }),
          suggestionsApi.listSuggestions(familyId, {
            createdBy,
            status: SuggestionStatusEnum.ChangesRequested,
            targetType: 'family_member',
            targetId: familyMemberId,
            page: 1,
            pageSize: 1
          })
        ]);

        const pendingTotal =
          pendingResult.success && pendingResult.data
            ? (pendingResult.data.total ?? pendingResult.data.items?.length ?? 0)
            : 0;
        const changesTotal =
          changesResult.success && changesResult.data
            ? (changesResult.data.total ?? changesResult.data.items?.length ?? 0)
            : 0;

        self.memberPendingFamilyMemberId = familyMemberId;
        self.memberPendingTotal = pendingTotal + changesTotal;
        self.memberPendingHasChangesRequested = changesTotal > 0;
      } catch {
        self.memberPendingFamilyMemberId = familyMemberId;
        self.memberPendingTotal = 0;
        self.memberPendingHasChangesRequested = false;
      } finally {
        self.isLoadingMemberPending = false;
      }
    }),

    loadSuggestionDetail: flow(function* (familyId: string, suggestionId: string) {
      self.isLoadingDetail = true;
      self.detailError = null;

      try {
        const result = yield suggestionsApi.getSuggestion(familyId, suggestionId);
        if (result.success && result.data) {
          self.selectedSuggestionDetail = result.data;
          self.suggestionsById.set(result.data.id, createSuggestionModelFromResponse(result.data));
        } else {
          self.detailError = result.error?.getUserMessage?.() ?? 'Failed to load suggestion';
        }
      } catch (error) {
        self.detailError = error instanceof Error ? error.message : 'Failed to load suggestion';
      } finally {
        self.isLoadingDetail = false;
      }
    }),

    cancelSuggestion: flow(function* (familyId: string, suggestionId: string) {
      self.isCancelling = true;
      self.error = null;

      try {
        const result = yield suggestionsApi.cancelSuggestion(familyId, suggestionId);
        if (result.success && result.data) {
          self.suggestionsById.set(result.data.id, createSuggestionModelFromResponse(result.data));
          const idx = self.myList.findIndex((item) => item.id === suggestionId);
          if (idx >= 0) {
            self.myList[idx] = toPreview(result.data);
          }
          if (self.selectedSuggestionId === suggestionId) {
            self.selectedSuggestionDetail = result.data;
          }
          return { success: true, data: result.data };
        }
        const message = result.error?.getUserMessage?.() ?? 'Failed to cancel suggestion';
        self.error = message;
        return { success: false, error: result.error ?? new Error(message) };
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to cancel suggestion';
        self.error = message;
        return { success: false, error: error instanceof Error ? error : new Error(message) };
      } finally {
        self.isCancelling = false;
      }
    })
  }));

export const suggestionsStore = SuggestionsStore.create({});
export type SuggestionsStoreType = Instance<typeof SuggestionsStore>;

/** Roles that may create suggestions at family level (matrix: all except viewer). */
export function canHaveAuthorSuggestions(familyRole: string | null | undefined): boolean {
  return familyRole === 'owner' || familyRole === 'admin' || familyRole === 'editor' || familyRole === 'contributor';
}

export { ACTIONABLE_STATUSES };
